"use client";

import Sticker from "@/app/(general-header)/botb-pass/components/Sticker";
import useBotBPassWidget from "../../hooks/useBotBPassWidget";
import "./botb-pass-widget.css";

/** Lands on the plans section of /botb-pass, tagged so GA can attribute it. */
const PLANS_URL = "/botb-pass?utm_medium=lifestyle_prizes#main-plans-section";

const viewPlansCta = (
  <a
    href={PLANS_URL}
    className="theme-btn theme-btn-primary theme-btn-sm lg:theme-btn-2lg w-full lg:w-[200px] uppercase">
    View plans
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="size-4 lg:size-5">
      <path
        d="M4.66797 4.66699L8.0013 8.00033L4.66797 11.3337M8.66797 4.66699L12.0013 8.00033L8.66797 11.3337"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </a>
);

/**
 * BOTB Pass cross-sell that closes out /prizes/lifestyle.
 *
 * Deliberately thin: the headline and the four stat tiles are the same Umbraco
 * HTML the /botb-pass hero renders, the offer card is /botb-pass's own
 * `Sticker`, and the small print is the legal text from its `Notes`. This
 * component only supplies the layout — a rounded card on the page's 1320px
 * content track instead of the full-bleed hero — plus the CTA, which navigates
 * to the plans rather than scrolling to them as it does on /botb-pass.
 */
export default function BotBPassWidget() {
  const { banner, milestonesHtml, subscriptionData } = useBotBPassWidget();

  // Nothing worth reserving space for below the grid until the copy lands.
  if (!banner) return null;

  return (
    <section className="botb-pass-widget flex flex-col w-full pt-8 lg:pt-6 lg:pb-4 font-jost">
      <div className="w-full px-4 lg:px-5">
        <div className="w-full max-w-[1320px] mx-auto">
          <div className="relative flex flex-col items-center justify-center w-full min-h-[428px] lg:min-h-[399px] rounded-2xl overflow-hidden">
            <picture className="absolute inset-0">
              {banner.DesktopImage && <source srcSet={banner.DesktopImage} media="(min-width: 1024px)" />}
              <img
                src={banner.MobileImage}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="size-full object-cover"
              />
            </picture>

            <div className="relative z-[1] flex flex-col items-center w-full px-3 lg:px-5 py-6 lg:py-10 gap-6 lg:gap-8">
              {/* Copy and offer sit on one row from lg, bottom-aligned so the
                  headline baseline meets the foot of the offer card. */}
              <div className="flex lg:flex-row flex-col items-center lg:items-end w-full max-w-[938px] gap-6 lg:gap-8">
                <div
                  className="flex flex-col w-full lg:w-[453px] shrink-0"
                  dangerouslySetInnerHTML={{ __html: banner.Html }}
                />
                <div className="flex flex-col w-full lg:flex-1 min-w-0">
                  <Sticker
                    subscriptionData={subscriptionData}
                    subscriptionDataLoading={!subscriptionData}
                    cta={viewPlansCta}
                  />
                </div>
              </div>

              {milestonesHtml && (
                <div className="w-full max-w-[938px]" dangerouslySetInnerHTML={{ __html: milestonesHtml }} />
              )}
            </div>
          </div>

          {subscriptionData?.header?.legalText && (
            <div className="flex flex-col items-center w-full pt-4 pb-8">
              <p className="text-[11px] leading-[14px] tracking-[0] text-gray-5 text-center">
                {subscriptionData.header.legalText}
              </p>
              <a
                href="/terms"
                title="Terms and Conditions apply"
                className="text-[11px] leading-[14px] text-gray-5 text-center underline cursor-pointer">
                Terms and Conditions apply.
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
